import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlusCircle, Trash, Receipt, FilePdf, Paperclip, X } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = [
  { v: "viaticos", l: "Viáticos" },
  { v: "transporte", l: "Transporte" },
  { v: "alojamiento", l: "Alojamiento" },
  { v: "alimentacion", l: "Alimentación" },
  { v: "otros", l: "Otros" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.l]));

const emptyForm = () => ({
  employee_id: "", date: new Date().toISOString().slice(0, 10), category: "viaticos",
  amount: "", currency: "MXN", description: "", evidence: [],
});

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function ReimbursementsTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/hr/employees").then((r) => setEmps(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterEmp !== "all") params.set("employee_id", filterEmp);
    if (filterCat !== "all") params.set("category", filterCat);
    api.get(`/hr/reimbursements?${params}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [filterEmp, filterCat]);
  useEffect(() => { load(); }, [load]);

  const addFiles = async (files) => {
    const list = Array.from(files || []).slice(0, 4 - form.evidence.length);
    for (const f of list) {
      try {
        let data;
        if (f.type.startsWith("image/")) {
          const { file: cf } = await compressImage(f);
          data = await fileToDataUrl(cf);
        } else if (f.type === "application/pdf" && f.size <= 2_000_000) {
          data = await fileToDataUrl(f);
        } else {
          toast.error(`${f.name}: use imágenes o PDF hasta 2MB`);
          continue;
        }
        setForm((p) => ({ ...p, evidence: [...p.evidence, { filename: f.name, content_type: f.type, data }] }));
      } catch { toast.error(`No se pudo procesar ${f.name}`); }
    }
  };

  const save = async () => {
    if (!form.employee_id || !form.amount) { toast.error("Colaborador y monto son obligatorios"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) await api.put(`/hr/reimbursements/${editing}`, payload);
      else await api.post("/hr/reimbursements", payload);
      toast.success("Reembolso guardado");
      setOpen(false); setEditing(null); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este reembolso y su evidencia?")) return;
    try { await api.delete(`/hr/reimbursements/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/hr/exports/reimbursements", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "reporte_reembolsos.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const total = items.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="hr-reimbursements">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterEmp} onValueChange={setFilterEmp}>
          <SelectTrigger className="w-56 h-9" data-testid="reimb-filter-emp"><SelectValue placeholder="Colaborador" /></SelectTrigger>
          <SelectContent className="bg-white max-h-64">
            <SelectItem value="all">Todos los colaboradores</SelectItem>
            {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-40 h-9" data-testid="reimb-filter-cat"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todas</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs font-bold text-slate-600 bg-slate-100 rounded-full px-3 py-1.5" data-testid="reimb-total">
          Total: {total.toLocaleString("es-PE", { minimumFractionDigits: 2 })}
        </span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-1.5" data-testid="reimb-pdf-btn">
          <FilePdf size={14} weight="bold" /> Reporte PDF
        </Button>
        {canManage && (
          <Button data-testid="reimb-new-btn" onClick={() => { setEditing(null); setForm(emptyForm()); setOpen(true); }}
                  className="bg-blue-900 hover:bg-blue-800 gap-2">
            <PlusCircle size={14} weight="bold" /> Nuevo reembolso
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">
            <Receipt size={22} className="inline mr-2 text-slate-300" />Sin reembolsos registrados
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reimb-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Colaborador</th>
                  <th className="text-left px-3 py-2">Categoría</th>
                  <th className="text-left px-3 py-2">Descripción</th>
                  <th className="text-right px-3 py-2">Monto</th>
                  <th className="text-center px-3 py-2">Evidencia</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it.id} data-testid={`reimb-row-${it.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{it.date}</td>
                    <td className="px-3 py-2">
                      <p className="font-semibold text-slate-900">{it.employee_name}</p>
                      <p className="text-[10px] text-slate-400">{it.employee_position} · Cuadr. {it.employee_crew}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{CAT_LABEL[it.category] || it.category}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[240px] truncate" title={it.description}>{it.description}</td>
                    <td className="px-3 py-2 text-right font-semibold">{(it.amount || 0).toLocaleString("es-PE", { minimumFractionDigits: 2 })} <span className="text-[10px] text-slate-400">{it.currency}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center gap-1">
                        {(it.evidence || []).map((ev, i) => (
                          ev.content_type?.startsWith("image/") ? (
                            <button key={i} onClick={() => setLightbox(ev)} data-testid={`reimb-evidence-${it.id}-${i}`}>
                              <img src={ev.data} alt={ev.filename} className="h-9 w-9 object-cover rounded border border-slate-200 hover:ring-2 hover:ring-blue-400" />
                            </button>
                          ) : (
                            <a key={i} href={ev.data} download={ev.filename} className="h-9 w-9 rounded border border-slate-200 flex items-center justify-center text-red-600 bg-red-50" title={ev.filename}>
                              <FilePdf size={16} weight="fill" />
                            </a>
                          )
                        ))}
                        {!(it.evidence || []).length && <span className="text-[10px] text-slate-300">—</span>}
                      </div>
                    </td>
                    {canManage && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => { setEditing(it.id); setForm({ ...emptyForm(), ...it, amount: String(it.amount) }); setOpen(true); }}
                                className="text-slate-400 hover:text-blue-800 mr-2 text-xs font-semibold" data-testid={`reimb-edit-${it.id}`}>Editar</button>
                        <button onClick={() => remove(it.id)} data-testid={`reimb-delete-${it.id}`} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm()); } }}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} reembolso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline-label">Colaborador</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="reimb-form-emp"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.position}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">Fecha</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reimb-form-date" /></div>
              <div><Label className="overline-label">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="reimb-form-cat"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="overline-label">Monto</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="reimb-form-amount" /></div>
              <div><Label className="overline-label">Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="reimb-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["MXN", "USD", "PEN", "CLP", "COP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="overline-label">Descripción</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Ej. Pasajes terrestres campamento — ciudad" data-testid="reimb-form-desc" /></div>
            <div>
              <Label className="overline-label">Evidencia (fotos o PDF, máx 4)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {form.evidence.map((ev, i) => (
                  <div key={i} className="relative">
                    {ev.content_type?.startsWith("image/") ? (
                      <img src={ev.data} alt={ev.filename} className="h-16 w-16 object-cover rounded border border-slate-200" />
                    ) : (
                      <div className="h-16 w-16 rounded border border-slate-200 flex items-center justify-center text-red-600 bg-red-50"><FilePdf size={22} weight="fill" /></div>
                    )}
                    <button onClick={() => setForm({ ...form, evidence: form.evidence.filter((_, j) => j !== i) })}
                            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={9} weight="bold" /></button>
                  </div>
                ))}
                {form.evidence.length < 4 && (
                  <button onClick={() => fileRef.current?.click()} data-testid="reimb-add-evidence"
                          className="h-16 w-16 rounded border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-600">
                    <Paperclip size={16} /><span className="text-[9px]">Adjuntar</span>
                  </button>
                )}
                <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
                       data-testid="reimb-evidence-input"
                       onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </div>
            </div>
            <Button onClick={save} disabled={busy || !form.employee_id || !form.amount}
                    className="bg-blue-900 hover:bg-blue-800 w-full" data-testid="reimb-form-save">
              {busy ? "Guardando…" : editing ? "Actualizar" : "Registrar reembolso"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="reimb-lightbox">
          <img src={lightbox.data} alt={lightbox.filename} className="max-h-full max-w-full rounded shadow-2xl" />
          <button className="absolute top-4 right-4 text-white/80 hover:text-white"><X size={26} weight="bold" /></button>
        </div>
      )}
    </div>
  );
}
